import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { theme, blueGrey, garnetRed } from '../../design-system/theme';

// Styled Components
const SidebarContainer = styled.aside<{ $collapsed: boolean }>`
  width: ${props => props.$collapsed ? '72px' : '260px'};
  min-height: 100vh;
  background: ${blueGrey[10]};
  color: white;
  transition: width 0.25s ease;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 100;

  @media (max-width: 900px) {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    transform: ${props => props.$collapsed ? 'translateX(-100%)' : 'translateX(0)'};
    width: 260px;
  }
`;

const LogoSection = styled.div<{ $collapsed: boolean }>`
  padding: ${props => props.$collapsed ? '20px 12px' : '20px 20px'};
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 72px;
`;

const LogoIcon = styled.div`
  width: 36px;
  height: 36px;
  background: ${blueGrey[8]};
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
  flex-shrink: 0;
`;

const LogoText = styled.span<{ $visible: boolean }>`
  font-size: 18px;
  font-weight: 700;
  white-space: nowrap;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.2s ease;
`;

const CollapseButton = styled.button`
  position: absolute;
  right: -14px;
  top: 80px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${blueGrey[9]};
  border: 2px solid ${blueGrey[10]};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: transform 0.2s ease;

  &:hover {
    background: ${blueGrey[8]};
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const NavSection = styled.nav`
  flex: 1;
  padding: 16px 0;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
`;

const NavGroup = styled.div`
  margin-bottom: 8px;
`;

const NavGroupTitle = styled.div<{ $collapsed: boolean }>`
  padding: 8px 20px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.4);
  display: ${props => props.$collapsed ? 'none' : 'block'};
`;

const NavItem = styled(Link)<{ $active: boolean; $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: ${props => props.$collapsed ? '14px 20px' : '12px 20px'};
  color: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.7)'};
  text-decoration: none;
  background: ${props => props.$active ? `rgba(54, 58, 80, 0.3)` : 'transparent'};
  border-left: 3px solid ${props => props.$active ? theme.primary : 'transparent'};
  transition: all 0.15s ease;
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: white;
  }

  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const NavLabel = styled.span<{ $visible: boolean }>`
  white-space: nowrap;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.15s ease;
  font-size: 14px;
  font-weight: 500;
`;

const NavBadge = styled.span<{ $visible: boolean }>`
  margin-left: auto;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  background: ${garnetRed[5]};
  color: white;
  border-radius: 10px;
  display: ${props => props.$visible ? 'inline' : 'none'};
`;

const Tooltip = styled.div<{ $show: boolean }>`
  position: absolute;
  left: 72px;
  background: ${blueGrey[10]};
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 13px;
  white-space: nowrap;
  z-index: 1000;
  opacity: ${props => props.$show ? 1 : 0};
  pointer-events: none;
  transition: opacity 0.15s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);

  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: 50%;
    transform: translateY(-50%);
    border: 6px solid transparent;
    border-right-color: ${blueGrey[10]};
  }
`;

const UserSection = styled.div<{ $collapsed: boolean }>`
  padding: ${props => props.$collapsed ? '16px 12px' : '16px 20px'};
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${theme.gradientPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
`;

const UserInfo = styled.div<{ $visible: boolean }>`
  overflow: hidden;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.15s ease;
`;

const UserName = styled.div`
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserRole = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
`;

// Navigation configuration
interface NavItemConfig {
  path: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
  platformOnly?: boolean;
}

interface NavGroupConfig {
  title: string;
  items: NavItemConfig[];
}

const navigationConfig: NavGroupConfig[] = [
  {
    title: 'Overview',
    items: [
      {
        path: '/admin/dashboard',
        label: 'Dashboard',
        icon: (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        ),
      },
      {
        path: '/admin/analytics',
        label: 'Analytics',
        icon: (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
      },
    ],
  },
  {
    title: 'Consent Management',
    items: [
      {
        path: '/admin/projects',
        label: 'Projects',
        icon: (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        ),
      },
      {
        path: '/admin/fiduciaries',
        label: 'Data Fiduciaries',
        icon: (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        ),
      },
      {
        path: '/admin/templates',
        label: 'Templates',
        icon: (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
      },
      {
        path: '/admin/consent-forms',
        label: 'Consent Forms',
        icon: (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
        ),
      },
      {
        path: '/admin/consents',
        label: 'Consent Artifacts',
        icon: (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      },
    ],
  },
  {
    title: 'Administration',
    items: [
      {
        path: '/admin/users',
        label: 'Users',
        icon: (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ),
      },
      {
        path: '/admin/withdrawals',
        label: 'Withdrawals',
        icon: (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        ),
      },
      {
        path: '/admin/erasure',
        label: 'Data Erasure',
        icon: (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        ),
      },
      {
        path: '/admin/audit-logs',
        label: 'Audit Logs',
        icon: (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        ),
      },
      {
        path: '/admin/signup-requests',
        label: 'Signup Requests',
        icon: (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
        ),
        platformOnly: true,
      },
      {
        path: '/admin/regions',
        label: 'Region Management',
        icon: (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        platformOnly: true,
      },
      {
        path: '/admin/help',
        label: 'Help & Support',
        icon: (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      },
    ],
  },
];

// Props
interface SidebarProps {
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, onCollapse }) => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<{ name?: string; email?: string; role?: string; tenantId?: string } | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem('userInfo');
    if (stored) {
      try {
        setUserInfo(JSON.parse(stored));
      } catch (e) {
        // ignore
      }
    }
  }, []);

  const isPlatformTenant = userInfo?.tenantId === '00000000-0000-0000-0000-000000000001';

  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    onCollapse?.(newState);
  };

  const isActive = (path: string) => {
    if (path === '/admin/fiduciaries') {
      return location.pathname.startsWith('/admin/fiduciaries') || location.pathname.startsWith('/admin/data-collectors');
    }
    if (path === '/admin/consent-forms') {
      return location.pathname.startsWith('/admin/consent-forms') || location.pathname.startsWith('/admin/generate-url');
    }
    return location.pathname.startsWith(path);
  };

  const getUserInitials = () => {
    if (userInfo?.name) {
      return userInfo.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    if (userInfo?.email) {
      return userInfo.email[0].toUpperCase();
    }
    return 'A';
  };

  const getDisplayName = () => {
    if (userInfo?.name) return userInfo.name;
    if (userInfo?.email) return userInfo.email.split('@')[0];
    return 'Admin User';
  };

  // Get data-tour attribute based on path
  const getTourAttribute = (path: string): string | undefined => {
    const tourMap: Record<string, string> = {
      '/admin/dashboard': 'nav-dashboard',
      '/admin/templates': 'nav-templates',
      '/admin/consent-forms': 'nav-consent-forms',
      '/admin/consents': 'nav-consents',
      '/admin/analytics': 'nav-analytics',
    };
    return tourMap[path];
  };

  return (
    <SidebarContainer $collapsed={isCollapsed} data-tour="sidebar">
      <LogoSection $collapsed={isCollapsed}>
        <LogoIcon>T</LogoIcon>
        <LogoText $visible={!isCollapsed}>Consent Platform</LogoText>
      </LogoSection>

      <CollapseButton onClick={toggleCollapse}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {isCollapsed ? (
            <path d="M9 18l6-6-6-6" />
          ) : (
            <path d="M15 18l-6-6 6-6" />
          )}
        </svg>
      </CollapseButton>

      <NavSection>
        {navigationConfig.map((group) => {
          const visibleItems = group.items.filter(item => !item.platformOnly || isPlatformTenant);
          if (visibleItems.length === 0) return null;

          return (
            <NavGroup key={group.title}>
              <NavGroupTitle $collapsed={isCollapsed}>{group.title}</NavGroupTitle>
              {visibleItems.map((item) => (
                <NavItem
                  key={item.path}
                  to={item.path}
                  $active={isActive(item.path)}
                  $collapsed={isCollapsed}
                  onMouseEnter={() => isCollapsed && setHoveredItem(item.path)}
                  onMouseLeave={() => setHoveredItem(null)}
                  data-tour={getTourAttribute(item.path)}
                >
                  {item.icon}
                  <NavLabel $visible={!isCollapsed}>{item.label}</NavLabel>
                  {item.badge && <NavBadge $visible={!isCollapsed}>{item.badge}</NavBadge>}
                  {isCollapsed && (
                    <Tooltip $show={hoveredItem === item.path}>
                      {item.label}
                      {item.badge && ` (${item.badge})`}
                    </Tooltip>
                  )}
                </NavItem>
              ))}
            </NavGroup>
          );
        })}
      </NavSection>

      <UserSection $collapsed={isCollapsed}>
        <UserAvatar>{getUserInitials()}</UserAvatar>
        <UserInfo $visible={!isCollapsed}>
          <UserName>{getDisplayName()}</UserName>
          <UserRole>{userInfo?.role || 'ADMIN'}</UserRole>
        </UserInfo>
      </UserSection>
    </SidebarContainer>
  );
};

export default Sidebar;
